import { useState, useEffect, useMemo } from 'react';
import { DashboardLayout } from '@/components/DashboardLayout';
import { Card, CardContent } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Badge } from '@/components/ui/badge';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Search, Download, X } from 'lucide-react';
import { ZatcaRowIcon } from '@/components/zatca/ZatcaRowIcon';
import { ZatcaDetailsDialog, ZATCA_LABELS } from '@/components/zatca/ZatcaDetailsDialog';
import { BranchFilter } from '@/components/filters/BranchFilter';
import { DateRangeFilter } from '@/components/filters/DateRangeFilter';
import { useBranchStore } from '@/stores';
import api from '@/lib/api';
import { toast } from 'sonner';

const TYPE_COLORS = { Invoice: 'bg-info/10 text-info', Proforma: 'bg-purple-50 text-purple-700', 'Credit Note': 'bg-orange-50 text-orange-700', 'Debit Note': 'bg-warning/10 text-warning', Receipt: 'bg-success/10 text-success' };
const STATUS_COLORS = { draft: 'bg-muted text-muted-foreground', sent: 'bg-info/20 text-info', paid: 'bg-success/20 text-success', partial: 'bg-warning/20 text-warning', unpaid: 'bg-error/20 text-error', received: 'bg-success/20 text-success', approved: 'bg-success/20 text-success', converted: 'bg-purple-100 text-purple-700', cancelled: 'bg-error/20 text-error' };

export default function SalesTransactionsPage() {
  const [txns, setTxns] = useState([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState('');
  const [typeFilter, setTypeFilter] = useState('all');
  const [customerFilter, setCustomerFilter] = useState(null); // iter419 — rollup click-to-filter
  const [branchId, setBranchId] = useState(null);
  const [range, setRange] = useState({});
  const [zatcaFilter, setZatcaFilter] = useState('all');
  const [zatcaTxn, setZatcaTxn] = useState(null);
  const branches = useBranchStore((s) => s.branches) || [];
  const branchName = (id) => branches.find((b) => b.id === id)?.name || '';

  useEffect(() => { fetchData(); }, []);
  const fetchData = async () => {
    try { const r = await api.get('/sales-transactions'); setTxns(r.data || []); } catch { toast.error('Failed to load'); } finally { setLoading(false); }
  };

  const filtered = txns.filter(t => {
    if (typeFilter !== 'all' && t.type !== typeFilter) return false;
    if (customerFilter && t.customer !== customerFilter) return false;
    if (branchId && t.branch_id !== branchId) return false;
    if (range.start_date && (t.date || '').slice(0, 10) < range.start_date) return false;
    if (range.end_date && (t.date || '').slice(0, 10) > range.end_date) return false;
    if (zatcaFilter !== 'all' && (t.zatca_status || 'NA') !== zatcaFilter) return false;
    if (search && !t.reference_id?.toLowerCase().includes(search.toLowerCase()) && !t.customer?.toLowerCase().includes(search.toLowerCase())) return false;
    return true;
  });

  const totalAmount = filtered.reduce((s, t) => s + (t.amount || 0), 0);
  const totalOutstanding = filtered.reduce((s, t) => s + (t.outstanding || 0), 0);

  // iter419 — Top 5 customers by outstanding (across the unfiltered set so
  // the strip stays meaningful even when the user has narrowed the table).
  const topOutstanding = useMemo(() => {
    const map = new Map();
    for (const t of txns) {
      const out = t.outstanding || 0;
      if (out <= 0) continue;
      const key = t.customer || '—';
      map.set(key, (map.get(key) || 0) + out);
    }
    return Array.from(map.entries())
      .sort((a, b) => b[1] - a[1])
      .slice(0, 5);
  }, [txns]);

  const exportCSV = () => {
    const esc = (v) => `"${String(v ?? '').replace(/"/g, '""')}"`;
    const header = 'Reference,Type,Customer,Branch,Date,Status,ZATCA,ZATCA UUID,Amount,Outstanding\n';
    const rows = filtered.map(t => [t.reference_id, t.type, t.customer, branchName(t.branch_id), t.date, t.status,
      ZATCA_LABELS[t.zatca_status] || '', t.zatca?.uuid || '', t.amount, t.outstanding].map(esc).join(',')).join('\n');
    const blob = new Blob([header + rows], { type: 'text/csv' });
    const a = document.createElement('a'); a.href = URL.createObjectURL(blob); a.download = 'sales_transactions.csv'; a.click();
  };

  return (
    <DashboardLayout>
      <div className="space-y-4" data-testid="sales-transactions-page">
        <div className="flex items-center justify-between">
          <div><h1 className="text-xl font-bold text-foreground">Sales Transactions</h1><p className="text-sm text-muted-foreground">Consolidated view of all sales documents</p></div>
          <Button variant="outline" onClick={exportCSV} data-testid="export-sales-csv"><Download size={14} className="mr-1" /> Export CSV</Button>
        </div>
        <div className="grid grid-cols-2 sm:grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-4">
          <Card><CardContent className="p-4"><p className="text-xs text-muted-foreground">Total Transactions</p><p className="text-xl font-bold">{filtered.length}</p></CardContent></Card>
          <Card><CardContent className="p-4"><p className="text-xs text-muted-foreground">Total Amount</p><p className="text-xl font-bold text-success">{totalAmount.toLocaleString('en', { minimumFractionDigits: 2 })}</p></CardContent></Card>
          <Card><CardContent className="p-4"><p className="text-xs text-muted-foreground">Outstanding</p><p className="text-xl font-bold text-warning">{totalOutstanding.toLocaleString('en', { minimumFractionDigits: 2 })}</p></CardContent></Card>
          <Card><CardContent className="p-4"><p className="text-xs text-muted-foreground">Document Types</p><p className="text-xl font-bold">{new Set(txns.map(t => t.type)).size}</p></CardContent></Card>
        </div>
        <div className="flex flex-wrap gap-3">
          <div className="relative flex-1 max-w-xs"><Search size={16} className="absolute left-3 top-1/2 -translate-y-1/2 text-muted-foreground" /><Input placeholder="Search ref or customer..." value={search} onChange={e => setSearch(e.target.value)} className="pl-9" /></div>
          <Select value={typeFilter} onValueChange={setTypeFilter}>
            <SelectTrigger className="w-48"><SelectValue placeholder="All Types" /></SelectTrigger>
            <SelectContent>
              <SelectItem value="all">All Types</SelectItem>
              <SelectItem value="Invoice">Invoice</SelectItem>
              <SelectItem value="Proforma">Proforma</SelectItem>
              <SelectItem value="Credit Note">Credit Note</SelectItem>
              <SelectItem value="Debit Note">Debit Note</SelectItem>
              <SelectItem value="Receipt">Receipt</SelectItem>
            </SelectContent>
          </Select>
          <BranchFilter value={branchId} onChange={setBranchId} testid="sales-txn-branch-filter" />
          <DateRangeFilter value={range} onChange={setRange} testid="sales-txn-date-filter" />
          <Select value={zatcaFilter} onValueChange={setZatcaFilter}>
            <SelectTrigger className="w-44" data-testid="sales-txn-zatca-filter"><SelectValue /></SelectTrigger>
            <SelectContent>
              <SelectItem value="all">All ZATCA statuses</SelectItem>
              {Object.entries(ZATCA_LABELS).map(([k, l]) => <SelectItem key={k} value={k}>{l}</SelectItem>)}
              <SelectItem value="NA">Not applicable</SelectItem>
            </SelectContent>
          </Select>
        </div>

        {/* iter419 — Outstanding by Customer rollup. Surfaces the worst-paying
            accounts at a glance; click a chip to filter the table to that
            customer; second click clears. Hidden when nothing outstanding. */}
        {topOutstanding.length > 0 && (
          <div className="flex flex-wrap items-center gap-2" data-testid="outstanding-by-customer-strip">
            <span className="text-[10px] uppercase tracking-wide text-muted-foreground font-semibold mr-1">
              Top outstanding
            </span>
            {topOutstanding.map(([cust, amt]) => (
              <button
                key={cust}
                type="button"
                onClick={() => setCustomerFilter(customerFilter === cust ? null : cust)}
                data-testid={`outstanding-chip-${cust}`}
                className={`inline-flex items-baseline gap-1.5 px-2.5 py-1 rounded-md text-xs border transition-colors ${
                  customerFilter === cust
                    ? 'bg-warning text-warning-foreground border-warning ring-1 ring-warning/40'
                    : 'bg-warning/10 text-warning hover:bg-warning/20 border-warning/30'
                }`}
                title={`Filter to ${cust}'s transactions`}
              >
                <span className="font-medium">{cust}</span>
                <span className="font-bold tabular-nums">{amt.toLocaleString('en', { minimumFractionDigits: 2 })}</span>
              </button>
            ))}
            {customerFilter && (
              <button
                type="button"
                onClick={() => setCustomerFilter(null)}
                data-testid="clear-customer-filter"
                className="inline-flex items-center gap-1 px-2 py-1 rounded-md text-xs text-muted-foreground hover:text-foreground hover:bg-muted/60 transition-colors"
              >
                <X size={12} /> Clear filter
              </button>
            )}
          </div>
        )}
        <Card><CardContent className="p-0">
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead className="bg-muted/50 border-b"><tr>
                <th className="text-left px-4 py-3 font-medium text-muted-foreground">Reference</th>
                <th className="text-left px-4 py-3 font-medium text-muted-foreground">Type</th>
                <th className="text-left px-4 py-3 font-medium text-muted-foreground">Customer</th>
                <th className="text-left px-4 py-3 font-medium text-muted-foreground">Branch</th>
                <th className="text-left px-4 py-3 font-medium text-muted-foreground">Date</th>
                <th className="text-left px-4 py-3 font-medium text-muted-foreground">Status</th>
                <th className="text-center px-4 py-3 font-medium text-muted-foreground">ZATCA</th>
                <th className="text-right px-4 py-3 font-medium text-muted-foreground">Amount</th>
                <th className="text-right px-4 py-3 font-medium text-muted-foreground">Outstanding</th>
              </tr></thead>
              <tbody>
                {loading ? <tr><td colSpan={9} className="text-center py-8 text-muted-foreground">Loading...</td></tr> :
                 filtered.length === 0 ? <tr><td colSpan={9} className="text-center py-8 text-muted-foreground">No transactions found</td></tr> :
                 filtered.map(t => (
                  <tr key={t.id} className="border-b hover:bg-muted/50">
                    <td className="px-4 py-3 font-mono text-xs font-medium">{t.reference_id}</td>
                    <td className="px-4 py-3"><Badge className={TYPE_COLORS[t.type] || 'bg-muted'}>{t.type}</Badge></td>
                    <td className="px-4 py-3">{t.customer}</td>
                    <td className="px-4 py-3 text-xs">{branchName(t.branch_id) || '—'}</td>
                    <td className="px-4 py-3 text-muted-foreground">{t.date}</td>
                    <td className="px-4 py-3"><Badge className={STATUS_COLORS[t.status] || 'bg-muted'}>{t.status}</Badge></td>
                    <td className="px-4 py-3 text-center" data-testid={`txn-zatca-${t.id}`}>{t.zatca_status
                      ? <button type="button" onClick={() => setZatcaTxn(t)} className="inline-flex items-center gap-1 text-[11px]" data-testid={`txn-zatca-btn-${t.id}`}>
                          <ZatcaRowIcon status={t.zatca_status} />{ZATCA_LABELS[t.zatca_status] || t.zatca_status}
                        </button>
                      : <span className="text-muted-foreground text-xs" title="Not an e-invoice document">N/A</span>}</td>
                    <td className="px-4 py-3 text-right font-medium">{(t.amount || 0).toLocaleString('en', { minimumFractionDigits: 2 })}</td>
                    <td className="px-4 py-3 text-right font-medium text-warning">{t.outstanding > 0 ? t.outstanding.toLocaleString('en', { minimumFractionDigits: 2 }) : '-'}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </CardContent></Card>
      </div>
      <ZatcaDetailsDialog txn={zatcaTxn} onClose={() => setZatcaTxn(null)} />
    </DashboardLayout>
  );
}
